import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { preventDefault, stopPropagation } from '../../../../utils/events'
import { convertBlobToBase64 } from '../../../../utils/file'
import { FileErrorTooLargeModal, useFileDropzone } from '../../../File'

import {
  ACCEPTED_IMAGE_TYPES,
  IMAGE_MAX_SIZE_MB,
  MAX_INLINE_SIZE,
} from './useEditorAddFile.constants'
import {
  getInlineImageDropFiles,
  toInlineImages,
} from './useEditorAddFile.utils'

export function useEditorAddFile({
  editorRef,
  enableInlineImageAttach = false,
  onChange,
  onDragOver,
  onDrop,
}) {
  const [bookmark, setBookmark] = useState(null)
  const [modal, setModal] = useState(null)

  const { t } = useTranslation()

  function handleCloseModal() {
    setModal(null)
  }

  async function insertBase64File(files) {
    await Promise.all(
      files.map(async file => {
        if (bookmark) {
          editorRef.current.selection.moveToBookmark(bookmark)
        }

        const dataUrl = file.base64 ?? (await convertBlobToBase64(file.blob))

        editorRef.current.undoManager.transact(() => {
          const img = editorRef.current.dom.create('img', {
            alt: file.name,
            src: dataUrl,
          })

          editorRef.current.selection.setNode(img)
        })
      })
    )
  }

  function handleFinishUpload(files) {
    insertBase64File(files)
  }

  const {
    getInputProps,
    getRootProps,
    open: handleOpenFileAttach,
  } = useFileDropzone({
    options: {
      accept: ACCEPTED_IMAGE_TYPES,
      maxSize: MAX_INLINE_SIZE,
      multiple: true,
      noClick: true,
    },
    useBase64: true,
    onCloseModal: handleCloseModal,
    onFinishUpload: handleFinishUpload,
    onSetModal: setModal,
  })

  function handleFocusOut() {
    const editor = editorRef.current

    if (editor?.selection) {
      const currentBookmark = editor.selection.getBookmark()

      setBookmark(currentBookmark)
    }
  }

  function handleInsertImageFromWysiwyg() {
    if (editorRef.current?.selection) {
      setBookmark(editorRef.current.selection.getBookmark())
    }

    handleOpenFileAttach()
  }

  function handleChangeAfterResize(editor) {
    onChange(editor.getContent())
  }

  async function insertInlineImages(files) {
    if (files.length > 0) {
      handleFinishUpload(await toInlineImages(files))
    }
  }

  function handleConfirmTooLargeModal(files) {
    return event => {
      stopPropagation(event)
      handleCloseModal()

      insertInlineImages(files)
    }
  }

  function handleInlineFilesFromAnySource(fileList) {
    const { acceptedFiles, tooLargeFileNames } =
      getInlineImageDropFiles(fileList)

    if (tooLargeFileNames.length > 0) {
      setModal(
        <FileErrorTooLargeModal
          files={tooLargeFileNames}
          sizeLimit={IMAGE_MAX_SIZE_MB}
          onConfirm={handleConfirmTooLargeModal(acceptedFiles)}
        />
      )
    } else {
      insertInlineImages(acceptedFiles)
    }
  }

  function setup(editor) {
    if (enableInlineImageAttach) {
      editor.ui.registry.addButton('customImageUploading', {
        icon: 'image',
        tooltip: t('File.InsertImageToEditorToolKey'),
        onAction: handleInsertImageFromWysiwyg,
      })
      editor.on('ObjectResized', () => handleChangeAfterResize(editor))
      editor.on('dragOver', e => {
        preventDefault(e)
        // TinyMCE reacts to dragover like hover over table and opens contextual UI.
        // We must block internal handlers while dragging files.
        e.stopImmediatePropagation()

        onDragOver?.(e)
      })
      editor.on('drop', e => {
        preventDefault(e)
        stopPropagation(e)

        onDrop?.()

        const transferFiles = e.dataTransfer?.files

        if (transferFiles?.length > 0) {
          handleInlineFilesFromAnySource([...transferFiles])
        }
      })
    }
  }

  return {
    getInputProps,
    getRootProps,
    handleFocusOut,
    modal,
    setup,
  }
}
